import { useState } from "react";

import { CellStatus } from "../../primitives";
import { usePatchReportCell } from "../../../hooks/usePatchReportCell";

import { HumanInputUIComponent } from "./HumanUIComponent";

import {
  HumanConfiguration,
  RatingUIElement,
  ReportCell,
  ReportColumn,
  SliderUIElement,
} from "@/types/evaluate";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

const getTextValue = (
  uiComponent: string,
  value?: string | number,
  config?: HumanConfiguration,
) => {
  if (value === null || value === undefined) {
    return "-";
  }

  switch (uiComponent) {
    case "text":
      return value;
    case "slider": {
      const sliderConfig = config?.ui_element as SliderUIElement;
      return `${value} / ${sliderConfig.max}`;
    }
    case "rating": {
      const ratingConfig = config?.ui_element as RatingUIElement;
      return `${value} / ${ratingConfig.max}`;
    }
    case "choice":
    case "number":
      return value;
    default:
      return "-";
  }
};

export const HumanInputCell = ({
  cell,
  column,
}: {
  cell: ReportCell;
  column: ReportColumn;
  isColumnSelected?: boolean;
}) => {
  const config: HumanConfiguration = column.configuration;
  const dataType = config?.data_type;
  const uiComponent = config?.ui_element?.component;
  const cellValue = cell?.value?.value;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [value, setValue] = useState<any>(cellValue);
  const status = cell?.status;
  const isCompleted = status === CellStatus.COMPLETED;

  const { patchReportCell, isLoading } = usePatchReportCell(cell?.id);

  return (
    <div className="flex items-center justify-center gap-y-2 pt-1">
      <span className="max-w-[256px] whitespace-pre-wrap text-xs">
        {getTextValue(uiComponent, cellValue, config)}
      </span>
      <Popover>
        <PopoverTrigger>
          <div className="flex h-full flex-col items-center justify-center px-2 py-1">
            <Button size="sm" variant="outline">
              Edit
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent className="flex flex-col items-center justify-center gap-y-2">
          <HumanInputUIComponent
            uiComponent={uiComponent}
            dataType={dataType}
            config={config}
            value={value}
            setValue={setValue}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              patchReportCell({
                value: value,
              });
            }}
            isLoading={isLoading}
          >
            {isCompleted ? "Overwrite" : "Save"}
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  );
};
